import { TitleCasePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import clone from 'clone-deep';
import { Guid } from 'guid-typescript';
import { BehaviorSubject, EMPTY, Observable, Subject, Subscription, interval as observableInterval, of, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, distinctUntilKeyChanged, first, mergeMap, takeWhile, tap } from 'rxjs/operators';
import { isOldSingleFileUpload, isOldWorkflowStarter } from '../../../utils/utils';
import { getWorkflowRunStatus, setRunStepInputs } from '../../../utils/workflow/workflow-runner-utils';
import { ModuleInputType } from '../enums/module-input-type.enum';
import { RunState } from '../enums/run-state.enum';
import { StepByStepRunState } from '../enums/step-by-step-run-state.enum';
import { StepType } from '../enums/step-type.enum';
import { WorkflowRunInitType } from '../enums/workflow-run-init.enum';
import { WorkflowRunMode } from '../enums/workflow-run-mode.enum';
import { WorkflowRunType } from '../enums/workflow-run-type.enum';
import { WorkflowType } from '../enums/workflow-type.enum';
import { ModuleRunStatus } from '../models/module-run-status.model';
import { ModuleParameter, StepOutputParameter, StepParameter } from '../models/parameter.model';
import { Result } from '../models/result.model';
import { RunStep, StepInput } from '../models/run-step.model';
import { SingleResponse } from '../models/single-response.model';
import { Step } from '../models/step.model';
import { WorkflowRunStatusResult } from '../models/workflow-run-status.model';
import { WorkflowRunStepStatus } from '../models/workflow-run-step-status.model';
import { WorkflowRunnerState } from '../models/workflow-runner-state.model';
import { WorkflowNode, WorkflowSpecificationModel } from '../models/workflow-specification';
import { WorkflowV1Read, WorkflowV1Run } from '../models/workflow-v1-read.model';
import { Workflow } from '../models/workflow.model';
import { DomainFactoryService } from '../services/domain-factory.service';
import { UserNotificationService } from '../services/user-notification.service';
import { UserWorkspaceService } from '../services/user-workspace.service';
import { WorkflowRunApiService } from '../services/workflow-run-api.service';
import { WorkflowStateService } from '../services/workflow-state.service';
import { WorkflowV1Service } from '../services/workflow-v1.service';
import { TrainingMethod } from '../types/training-method.type';
import { ResultsService } from './results/results.service';

export interface IWorkflowRunService {
    runInitType?: WorkflowRunInitType;
    runMode?: WorkflowRunMode;
    runState: RunState;
    workflow?: WorkflowV1Run;
    workflowRunId?: string;
    workflowRunInstanceId?: string;
    latestModules?: ModuleRunStatus[];
    workflowName?: string;
    isLastStepActive: boolean;
    runStatus$: Observable<WorkflowRunnerState>;
    moduleUpdate$: Observable<ModuleRunStatus[]>;
    triggerStepUpdate$: Observable<void>;
    activeStepByStepIndex$: Observable<number | undefined>;
    stepByStepRunState$: Observable<StepByStepRunState>;
    runWorkflow({ runSteps, isStepByStepRun }: { runSteps: RunStep[]; isStepByStepRun: boolean }): Observable<{ workflowRunId: string }>;
    completeStep(runSteps: RunStep[], moduleId: number, stepId: number): Observable<WorkflowRunStepStatus>;
    executeStep(runStep: RunStep, input: StepInput, stepId: number): Observable<WorkflowRunStepStatus>;
    deleteRun(): Observable<Object>;
    saveResult(title?: string, description?: string): Observable<Result>;
    pollWorkflowStatus(workflowRunArchiveId: string): void;
    setPendingStatus(): void;
    getFirstConnectedRunStepParamType(step: RunStep): string | undefined;
}

/** @README Detailed documentation is available in the WorkflowRunner.md */
@UntilDestroy()
@Injectable()
export class WorkflowRunService implements OnDestroy, IWorkflowRunService {
    set runInitType(runInitType: WorkflowRunInitType | undefined) {
        this._runInitType = runInitType;
    }
    get runInitType(): WorkflowRunInitType | undefined {
        return this._runInitType;
    }

    set runMode(runMode: WorkflowRunMode | undefined) {
        this._runMode = runMode;
    }
    get runMode(): WorkflowRunMode | undefined {
        return this._runMode;
    }

    get runState(): RunState {
        return this.runStatus$$.value.state;
    }

    get runStatus$(): Observable<WorkflowRunnerState> {
        return this.runStatus$$.asObservable().pipe(distinctUntilKeyChanged('message'));
    }

    get moduleUpdate$(): Observable<ModuleRunStatus[]> {
        return this.moduleUpdate$$.asObservable();
    }

    get triggerStepUpdate$(): Observable<void> {
        return this.triggerStepUpdate$$.asObservable();
    }

    get activeStepByStepIndex$(): Observable<number | undefined> {
        return this.activeStepByStepIndex$$.asObservable().pipe(distinctUntilChanged());
    }

    get stepByStepRunState$(): Observable<StepByStepRunState> {
        return this.stepByStepState$$.asObservable().pipe(distinctUntilChanged());
    }

    set workflow(workflow: WorkflowV1Run | undefined) {
        this._workflow = workflow;
    }
    get workflow(): WorkflowV1Run | undefined {
        return this._workflow;
    }

    get workflowName(): string | undefined {
        return this._workflow ? this._workflow.title : '';
    }

    set workflowRunId(workflowRunId: string | undefined) {
        this._workflowRunId = workflowRunId;
    }
    get workflowRunId(): string | undefined {
        return this._workflowRunId;
    }

    get workflowRunInstanceId(): string | undefined {
        return this._workflowRunInstanceId;
    }

    get latestModules(): ModuleRunStatus[] {
        return this._latestModules;
    }

    get isLastStepActive(): boolean {
        if (this.runSteps === undefined) {
            return false;
        }
        return this.activeStepByStepIndex$$.value === this.runSteps.length - 1;
    }

    set steps(steps: RunStep[]) {
        this.runSteps = steps;
    }

    private readonly pollingIntervalInMs: number = 2000;

    private _workflow?: WorkflowV1Run;
    private _workflowRunId?: string;
    private _workflowRunInstanceId?: string;
    private _latestModules: ModuleRunStatus[];
    private _runInitType?: WorkflowRunInitType;
    private _runMode?: WorkflowRunMode;
    private result?: Result;
    private runSteps?: RunStep[];
    private lengthOfStepByStepModulesRan: number = 0;
    private waitForModuleToStartRunning: boolean = false;

    private runStatus$$: BehaviorSubject<WorkflowRunnerState> = new BehaviorSubject({ message: undefined, state: RunState.Idle });
    private stepByStepState$$: BehaviorSubject<StepByStepRunState> = new BehaviorSubject(StepByStepRunState.Idle);
    private moduleUpdate$$: Subject<ModuleRunStatus[]> = new Subject();
    private triggerStepUpdate$$: Subject<void> = new Subject();
    private activeStepByStepIndex$$: BehaviorSubject<number | undefined> = new BehaviorSubject(undefined);

    private pollWorkflowStatusSubscription?: Subscription;

    private readonly translationKeys = {
        synchronizing: 'WORKFLOWS.RUNNER.SYNCHRONIZING_RUN',
        preparing: 'WORKFLOWS.RUNNER.PREPARING_RUN',
        startingFailed: 'WORKFLOWS.RUNNER.STARTING_FAILED',
        finishedSuccessfully: 'WORKFLOWS.RUNNER.FINISHED_SUCCESSFULLY',
        runEnqueued: 'WORKFLOWS.RUNNER.RUN_ENQUEUED',
        runQueryDelayed: 'WORKFLOWS.RUNNER.RUN_QUERY_DELAYED',
        runFailed: 'WORKFLOWS.RUNNER.RUN_FAILED',
        runStatus: 'WORKFLOWS.RUNNER.RUN_STATUS',
        notStarted: 'WORKFLOWS.RUNNER.STEP_BY_STEP_RUN_NOT_STARTED',
        runFailedAtModule: 'WORKFLOWS.RUNNER.RUN_FAILED_AT_MODULE',
        runFailedCompileError: 'WORKFLOWS.RUNNER.RUN_FAILED_COMPILE_ERROR',
        runFailedInteractiveWorkflowAlreadyRunning: 'WORKFLOWS.RUNNER.RUN_FAILED_INTERACTIVE_WORKFLOW_ALREADY_RUNNING',
        runFailedTooManyInteractiveWorkflow: 'WORKFLOWS.RUNNER.RUN_FAILED_TOO_MANY_INTERACTIVE_WORKFLOW',
        preparingResults: 'WORKFLOWS.RUNNER.PREPARING_RESULTS',
        loadingResults: 'WORKFLOWS.RUNNER.LOADING_RESULTS',
        resultsAvailable: 'WORKFLOWS.RUNNER.RESULT_AVAILABLE',
        pollingStatusFailed: 'WORKFLOWS.RUNNER.NOTIFICATION.POLLING_STATUS_FAILED',
        pollingResultsFailed: 'WORKFLOWS.RUNNER.NOTIFICATION.POLLING_RESULTS_FAILED',
        preparingModuleRun: 'WORKFLOWS.RUNNER.PREPARING_MODULE_RUN',
        savingWorkflowFailed: 'WORKFLOWS.RUNNER.SAVING_WORKFLOW_FAILED',
    };

    constructor(
        private resultsService: ResultsService,
        private userWorkspaceService: UserWorkspaceService,
        private titleCasePipe: TitleCasePipe,
        private translateService: TranslateService,
        private userNotificationService: UserNotificationService,
        private workflowRunApiService: WorkflowRunApiService,
        private workflowV1Service: WorkflowV1Service,
        private workflowStateService: WorkflowStateService,
        private domainFactory: DomainFactoryService,
    ) {
        this.runStatus$
            .pipe(
                tap((runStatus: WorkflowRunnerState) => {
                    if (runStatus.state === RunState.Complete && this._workflowRunId !== undefined) {
                        this.pollWorkflowResults$(this._workflowRunId)
                            .pipe(
                                catchError((err: unknown) => {
                                    console.error('Failed to fetch workflow results', err);
                                    // @ts-ignore
                                    const errorMessage = err.message ? err.message : JSON.stringify(err);
                                    const title = this.translateService.instant(this.translationKeys.pollingResultsFailed);
                                    this.showPollingErrorNotification(errorMessage, title);
                                    return EMPTY;
                                }),
                                untilDestroyed(this),
                            )
                            .subscribe();
                    }

                    // Update result number in sidebar when workflow fails or completes
                    if (runStatus.state === RunState.Complete || runStatus.state === RunState.Failed) {
                        this.userWorkspaceService.updateMyResults$().subscribe();
                    }

                    if (runStatus.state === RunState.Failed && this._runMode === WorkflowRunMode.StepByStep) {
                        this.updateStepByStepRunState(StepByStepRunState.Failed);
                    }
                }),
                untilDestroyed(this),
            )
            .subscribe();
    }

    // we can use the onDestroy lifecycle as we provide the service only per component
    ngOnDestroy(): void {
        if (this.pollWorkflowStatusSubscription) {
            this.pollWorkflowStatusSubscription.unsubscribe();
        }

        this.runStatus$$.unsubscribe();
        this.stepByStepState$$.unsubscribe();
        this.moduleUpdate$$.unsubscribe();
        this.triggerStepUpdate$$.unsubscribe();
        this.activeStepByStepIndex$$.unsubscribe();
    }

    /**
     * Retrieves the type of the parameter which is connected to {step}.
     */
    getFirstConnectedRunStepParamType(step: RunStep): string | undefined {
        let connectedParamType: string | undefined = undefined;
        if (this._workflow != null && this._workflow.specification != null) {
            this._workflow.specification.workflow.flow.forEach((flow: WorkflowNode) => {
                const inputParams: Object | undefined = flow.run.input;
                if (inputParams != null && Object.entries(inputParams).length > 0) {
                    for (const [paramKey, paramValue] of Object.entries(inputParams)) {
                        if (paramKey != null && paramValue != null && paramValue.operation_id != null && paramValue.operation_id === step.flowId) {
                            connectedParamType = paramValue.output;
                        }
                    }
                }
            });
        }

        return connectedParamType;
    }

    saveWorkflowWithNewParameters(): Observable<SingleResponse<WorkflowV1Read>> {
        if (!this.workflow || !this.workflow.specification) {
            return throwError('Cannot save new workflow parameters as workflow is not defined');
        }

        if (this.workflow.visibility === 'Public') {
            // It is not possible to update public workflows
            return of();
        }

        const updatedWorkflowSpecification: WorkflowSpecificationModel | null = this.getUpdatedWorkflowSpecification(this.workflow.specification);

        if (updatedWorkflowSpecification === null) {
            return throwError('Cannot save new workflow parameters as workflow specification is invalid');
        }

        const workflowUpdateModel = {
            title: this.workflow.title,
            description: this.workflow.description,
            summary: this.workflow.summary,
            specification: updatedWorkflowSpecification,
        };
        return this.workflowV1Service.update(this.workflow.id, workflowUpdateModel);
    }

    runWorkflow({ runSteps, isStepByStepRun }: { runSteps: RunStep[]; isStepByStepRun: boolean }): Observable<{ workflowRunId: string }> {
        if (!this._workflow) {
            return throwError('Cannot start workflow run as workflow is not defined');
        }

        this.runSteps = runSteps;
        this._runMode = isStepByStepRun ? WorkflowRunMode.StepByStep : WorkflowRunMode.SingleAndBatchRun;
        this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.preparing), state: RunState.Pending });

        const workflowRunWillBeAReRun: boolean = this._workflowRunId !== undefined;

        const startWorkflowRunObservable$ = workflowRunWillBeAReRun
            ? this.startWorkflowReRun$(runSteps, isStepByStepRun)
            : this.startWorkflowRun(runSteps, isStepByStepRun);

        return startWorkflowRunObservable$.pipe(
            tap((res: { workflowRunId: string }) => {
                this._workflowRunId = res.workflowRunId;
                this.updateStepByStepRunState(StepByStepRunState.Running);
                this.pollWorkflowStatus(this._workflowRunId);
                // Create new steps to emit new event and trigger step validation
                this.triggerStepUpdate$$.next();
            }),
            catchError((error: unknown) => {
                if (error instanceof HttpErrorResponse) {
                    let errorMessage: string = this.translateService.instant(this.translationKeys.startingFailed);
                    if (error.error.type === 'InteractiveWorkflowAlreadyRunning') {
                        errorMessage = this.translateService.instant(this.translationKeys.notStarted);
                        this.runStatus$$.next({
                            message: errorMessage,
                            state: RunState.Idle,
                        });
                        this.updateStepByStepRunState(StepByStepRunState.Idle);
                    } else {
                        let errorContent = '';
                        if (error.error.type) {
                            if (error.error.type === 'InteractiveWorkflowAlreadyRunning') {
                                errorContent = this.translateService.instant(this.translationKeys.runFailedInteractiveWorkflowAlreadyRunning);
                            } else if (error.error.type === 'TooManyInteractiveWorkflow') {
                                errorContent = this.translateService.instant(this.translationKeys.runFailedTooManyInteractiveWorkflow);
                            }
                        } else {
                            if (error.error && error.error.errors && error.error.errors.length > 0) {
                                errorContent = error.error.errors[0].title;
                            }
                            errorMessage = error.error.Message;
                        }
                        this.runStatus$$.next({
                            message: errorMessage,
                            state: RunState.Failed,
                            failureInformation: { error: errorContent },
                        });
                        this.updateStepByStepRunState(StepByStepRunState.Failed);
                    }
                }
                return EMPTY;
            }),
        );
    }

    // We currently set the stepId to the moduleId. This will change if runner supports running multiple
    // modules at the same time
    completeStep(runSteps: RunStep[], moduleId: number, stepId: number = moduleId): Observable<WorkflowRunStepStatus> {
        if (this._workflowRunId === undefined) {
            return throwError('Cannot complete step as no workflowRunId is available');
        }

        if (stepId !== runSteps.length - 1) {
            this.waitForModuleToStartRunning = true;
        }

        // Set new run steps as they may have new parameters
        this.runSteps = runSteps;

        this.updateStepByStepRunState(StepByStepRunState.Running);

        return this.workflowRunApiService.completeModule(this._workflowRunId, stepId, moduleId).pipe(
            tap(
                () => {
                    this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.preparingModuleRun), state: RunState.Pending });
                    this.activeStepByStepIndex$$.next(moduleId + 1);
                    this.pollWorkflowStatus(this._workflowRunId);
                },
                () => this.updateStepByStepRunState(StepByStepRunState.Failed),
            ),
        );
    }

    executeStep(runStep: RunStep, input: StepInput, stepId?: number): Observable<WorkflowRunStepStatus> {
        if (this._workflowRunId === undefined) {
            return throwError('Cannot execute step as no workflowRunId is available');
        }

        // We currently set the stepId to the moduleId. This will change if runner supports running multiple modules at the same time
        if (stepId == null && this.activeStepByStepIndex$$.value !== undefined) {
            stepId = this.activeStepByStepIndex$$.value;
        } else if (stepId == null) {
            return throwError('Cannot execute step as no active step information is available');
        }

        this.waitForModuleToStartRunning = true;
        const { index } = runStep;

        this.updateStepByStepRunState(StepByStepRunState.Running);
        const inputParams = clone(input, true);
        // Remove referenced inputs on workflow re-run modules for step by step
        // If operation_id is not null only then it means it is connection parameter.
        Object.keys(inputParams)
            .filter(key => typeof inputParams[key] === 'object' && inputParams[key].operation_id != null && !Array.isArray(inputParams[key]))
            .forEach(key => delete inputParams[key]);
        return this.workflowRunApiService.executeModule(this._workflowRunId, stepId, index, inputParams).pipe(
            tap(
                () => {
                    this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.preparingModuleRun), state: RunState.Pending });
                    this.pollWorkflowStatus(this._workflowRunId);
                },
                () => this.updateStepByStepRunState(StepByStepRunState.Failed),
            ),
        );
    }

    deleteRun(): Observable<Object> {
        if (!this._workflowRunId) {
            return EMPTY; // we do not need to delete if there is no active run
        }
        return this.workflowRunApiService.deleteRun(this._workflowRunId).pipe(
            // 42507: The delete workflow run is currently not available from WFE so we ignore the errors at the moment
            catchError((): Observable<Object> => of()),
        );
    }

    saveResult(title?: string, description?: string): Observable<Result> {
        const result = this.result;
        if (!result) {
            return throwError('Cannot save as no result is available');
        }

        return this.resultsService.updateResult$(result.id, description || result.description || '', title || result.title || '').pipe(
            tap((res: Result) => {
                this.userWorkspaceService.updateMyResults$().subscribe();
                this.result = res;
            }),
        );
    }

    pollWorkflowStatus(workflowRunArchiveId?: string): void {
        const title = this.translateService.instant(this.translationKeys.pollingStatusFailed);

        if (!workflowRunArchiveId) {
            console.error('Cannot poll workflow status without valid workflowRunId');
            this.showPollingErrorNotification('Cannot poll workflow status without valid workflowRunId', title);
            return;
        }

        if (this.pollWorkflowStatusSubscription == null) {
            this.pollWorkflowStatusSubscription = this._pollWorkflowStatus$(workflowRunArchiveId)
                .pipe(
                    catchError((err: unknown) => {
                        console.error('Failed to pull workflow status', err);
                        // @ts-ignore
                        const errorMessage = err.message ? err.message : JSON.stringify(err);
                        this.showPollingErrorNotification(errorMessage, title);
                        return EMPTY;
                    }),
                )
                .subscribe();
        }
    }

    setPendingStatus(): void {
        this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.synchronizing), state: RunState.Pending });
    }

    determineWorkflowRunTypeForNewDefineInput(runSteps: RunStep[], workflow?: Workflow): WorkflowRunType {
        let runType: WorkflowRunType = WorkflowRunType.SingleRun;

        if (workflow == null) {
            return runType;
        }
        const starterStep = workflow.steps[0];
        if (starterStep.type === StepType.WorkflowDefineInput) {
            starterStep.moduleVersion.outputParameters.forEach((param: ModuleParameter) => {
                workflow.steps.forEach((step: Step) => {
                    step.inputParameters.forEach((stepParam: StepParameter) => {
                        if (
                            stepParam instanceof StepOutputParameter &&
                            stepParam.sourceOutputKey === param.key &&
                            stepParam.sourceFlowId === starterStep.flowId
                        ) {
                            // Here we know that the workflowStarter step is connected to a single file parameter.
                            // Then, we need to check if we have one or several files passed, so that we can determine
                            // if this is a single or batch run.
                            if (stepParam.type === ModuleInputType.File) {
                                // get runStep, because there we can get the current selection of files
                                const runStep = runSteps.find((_step: RunStep) => _step.flowId === starterStep.flowId);
                                if (runStep != null) {
                                    // Iterate through inputs
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    Object.entries(runStep.input).forEach((entry: Array<any>) => {
                                        const value: string = entry[1];

                                        if (value != null && value !== '' && Array.isArray(value) && value.length > 0) {
                                            runType = WorkflowRunType.BatchRun;
                                        }
                                    });
                                }
                            }
                        }
                    });
                });
            });
        }

        return runType;
    }

    startWorkflowRun(
        runSteps: RunStep[],
        isStepByStepRun: boolean = false,
        workflowRunName?: string,
        classes?: string[],
        classesOverlap?: boolean,
        trainingType?: TrainingMethod,
        trainingResultId?: string,
        notifyWhenIsDone: boolean = false,
        zenCompatible: boolean = false,
        datasetId: string = Guid.EMPTY.toString(),
        workflowType: WorkflowType = WorkflowType.UNDEFINED,
        modelId?: string,
        trainingId?: string,
        datasetVersionId?: string,
    ): Observable<{ workflowRunId: string }> {
        let workflowInputs: StepInput[];

        try {
            const isBatchRunForNewDefineInput: boolean =
                this.determineWorkflowRunTypeForNewDefineInput(runSteps, this.workflowStateService.workflow!) === WorkflowRunType.BatchRun;
            workflowInputs = setRunStepInputs(runSteps, isBatchRunForNewDefineInput);
        } catch (error) {
            const errorMessage = this.translateService.instant(this.translationKeys.startingFailed);
            this.runStatus$$.next({
                message: errorMessage,
                state: RunState.Failed,
                failureInformation: { error: error.stack ? error.stack : error },
            });
            throw new Error(errorMessage);
        }

        const stepByStepFlowIds = isStepByStepRun ? this.getStepByStepFlowIds(runSteps) : [];

        return this.workflowRunApiService.startWorkflowRun(
            this.workflowStateService.workflow!.id,
            workflowInputs,
            stepByStepFlowIds,
            workflowRunName,
            classes,
            classesOverlap,
            trainingType,
            trainingResultId,
            notifyWhenIsDone,
            zenCompatible,
            datasetId,
            workflowType,
            modelId,
            trainingId,
            datasetVersionId,
        );
    }

    private getStepByStepFlowIds(runSteps: RunStep[]): string[] {
        return runSteps
            .filter((runStep: RunStep) => {
                // Take all steps, but exclude oldWorkflowStarter, WorkflowDefineInput, ModuleDefineInput, OldSingleFileUpload
                return (
                    !this.domainFactory.isWorkflowDefineInput(runStep.module.id) &&
                    !this.domainFactory.isModuleDefineInput(runStep.module.id) &&
                    !isOldWorkflowStarter(runStep.module.title, runStep.index) &&
                    !isOldSingleFileUpload(runStep.module.title, runStep.index)
                );
            })
            .map((runStep: RunStep) => runStep.flowId);
    }

    private startWorkflowReRun$(runSteps: RunStep[], isStepByStepRun: boolean = false): Observable<{ workflowRunId: string }> {
        if (!this._workflowRunId) {
            return throwError('Cannot start workflow rerun as no workflowRunId is available');
        }

        let workflowInputs;

        try {
            workflowInputs = setRunStepInputs(runSteps);
        } catch (error) {
            const errorMessage = this.translateService.instant(this.translationKeys.startingFailed);
            this.runStatus$$.next({
                message: errorMessage,
                state: RunState.Failed,
                failureInformation: { error: error.stack ? error.stack : error },
            });
            throw new Error(errorMessage);
        }

        const stepByStepFlowIds = isStepByStepRun ? this.getStepByStepFlowIds(runSteps) : [];

        return this.workflowRunApiService.triggerReRun(this._workflowRunId, workflowInputs, stepByStepFlowIds);
    }

    private showPollingErrorNotification(errorMessage: string, title: string): void {
        this.userNotificationService.showError(errorMessage, title, { disableTimeOut: true });
    }

    private _pollWorkflowStatus$(workflowRunId: string): Observable<WorkflowRunStatusResult> {
        return observableInterval(this.pollingIntervalInMs).pipe(
            mergeMap(() => this.workflowRunApiService.getWorkflowRunStatus(workflowRunId)),
            tap((workflowRun: WorkflowRunStatusResult) => {
                workflowRun.data.modules = workflowRun.data.modules.sort((a, b) => {
                    return a.moduleId > b.moduleId ? 1 : a.moduleId === b.moduleId ? 0 : -1;
                });
                this._latestModules = workflowRun.data.modules;
                this._workflowRunInstanceId = workflowRun.data.workflowRunInstanceId;
                this.moduleUpdate$$.next(workflowRun.data.modules);
                // In case the module has not yet started re-running, do not update the state of workflow runner
                if (this.waitForModuleToStartRunning && this.lengthOfStepByStepModulesRan >= workflowRun.data.modules.length) {
                    return;
                } else {
                    const workflowRunStatus = getWorkflowRunStatus(workflowRun.data, this.translateService, this.titleCasePipe, this.translationKeys);
                    this.runStatus$$.next(workflowRunStatus);
                    if (this._runMode === WorkflowRunMode.StepByStep) {
                        this.updateStepByStep(workflowRun);
                    }
                    this.waitForModuleToStartRunning = false;
                    this.lengthOfStepByStepModulesRan = workflowRun.data.modules.length;
                }
            }),
            takeWhile((workflowRun: WorkflowRunStatusResult) => {
                const isRunCompletedOrFailed = ['complete', 'failed', 'cancelled'].includes(workflowRun.data.state);
                if (isRunCompletedOrFailed) {
                    // Due to the polling interval it can happen that we don't have the results for the last step
                    // Therefore we generate new steps which have the final results
                    this.moduleUpdate$$.next(workflowRun.data.modules);
                }

                // In case the last module is set to running, we set the workflowRunState to running.
                // Normally that status is set when the user clicks the button to run the next module. But
                // since in some scenarios the module starts automatically (i.e. no proxy modules, i.e. moduleDefineInput)
                // we need to trigger it automatically
                if (
                    workflowRun.data.modules != null &&
                    workflowRun.data.modules.length > 0 &&
                    workflowRun.data.modules[workflowRun.data.modules.length - 1].state === 'running'
                ) {
                    this.updateStepByStepRunState(StepByStepRunState.Running);
                }

                return workflowRun && !isRunCompletedOrFailed;
            }),
        );
    }

    private updateStepByStep(workflowRun: WorkflowRunStatusResult): void {
        const indexOfActiveModule = this.getIndexOfActiveModule(workflowRun);

        this.activeStepByStepIndex$$.next(indexOfActiveModule);

        // Don't ever stop the polling of the status (not even if the workflow is idle),
        // because it can happen that the status does not yet return the latest module (in modules array)
        // even though it has already been started.
        if (workflowRun.data.state !== 'running' && workflowRun.data.state !== 'pending') {
            const moduleId = this.activeStepByStepIndex$$.value || 0;
            const translation = this.translateService.instant('WORKFLOWS.RUNNER.MODULE_IS_COMPLETE', { id: moduleId + 1 });
            if (this.runStatus$$.value.state !== RunState.Failed) {
                this.runStatus$$.next({ message: translation, state: RunState.Idle });
            }
            this.stopPolling();
        }

        // If the state of the last module is not running, update the stepByStepRunState to Idle
        // We have a glitch here: After pressing the "Run Next Module" button, we don't immediately get
        // the next module with the workflow state. So it might be that the state switches to Idle only at the
        // next polling interval. To solve that we introduced the this.waitingForNextPollingInterval flag.
        if (
            workflowRun.data.modules &&
            workflowRun.data.modules.length > 0 &&
            (workflowRun.data.modules[workflowRun.data.modules.length - 1].state !== 'running' ||
                workflowRun.data.modules[workflowRun.data.modules.length - 1].state !== 'waiting')
        ) {
            this.updateStepByStepRunState(StepByStepRunState.Idle);
        }
    }

    private stopPolling(): void {
        if (this.pollWorkflowStatusSubscription) {
            this.pollWorkflowStatusSubscription.unsubscribe();
        }
    }

    private getIndexOfActiveModule(workflowRun: WorkflowRunStatusResult): number | undefined {
        const { modules } = workflowRun.data;
        if (!modules || modules.length === 0) {
            return 0; // first module is active per default
        }
        const lastModule = modules[modules.length - 1];
        return lastModule.moduleId;
    }

    private pollWorkflowResults$(workflowRunId: string): Observable<Result> {
        this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.preparingResults), state: RunState.Running });

        return observableInterval(this.pollingIntervalInMs).pipe(
            mergeMap(() => this.resultsService.getByWorkflowRunId$(workflowRunId)),
            tap((result: Result) => {
                this.runStatus$$.next({ message: this.translateService.instant(this.translationKeys.loadingResults), state: RunState.Running });
                if (result && this.checkIfResultFilesAreReady(result)) {
                    this.userWorkspaceService.updateMyResults$().subscribe();
                    this.result = result;
                    this.runStatus$$.next({
                        message: this.translateService.instant(this.translationKeys.resultsAvailable),
                        state: RunState.ResultAvailable,
                    });
                }
            }),
            first((result: Result) => result != null && this.checkIfResultFilesAreReady(result)),
        );
    }

    private checkIfResultFilesAreReady(result: Result): boolean {
        return result.state !== 'pending';
    }

    private updateStepByStepRunState(state: StepByStepRunState): void {
        if (this._runMode === WorkflowRunMode.StepByStep) {
            this.stepByStepState$$.next(state);
        }
    }

    private getUpdatedWorkflowSpecification(workflowSpecification: WorkflowSpecificationModel): WorkflowSpecificationModel | null {
        const runSteps = this.runSteps;
        const updatedWorkflowSpecification = { ...workflowSpecification };

        if (!runSteps) {
            return null;
        }
        if (!updatedWorkflowSpecification.workflow || !workflowSpecification.workflow) {
            return null;
        }

        updatedWorkflowSpecification.workflow.flow = workflowSpecification.workflow.flow.map((flow: WorkflowNode) => {
            const runStep = runSteps.find((step: RunStep) => step.flowId === flow.id);
            if (runStep) {
                // We cannot just replace the whole flow run input object as it may also contain additional
                // information about in/outputs which are only evaluated in workflow editor
                for (const stepInput of Object.entries(runStep.input)) {
                    const [key, value] = stepInput;
                    if (flow.run && flow.run.input && flow.run.input[key] !== undefined) {
                        flow.run.input[key] = value;
                    }
                }
            }
            return flow;
        });

        return updatedWorkflowSpecification;
    }
}
